import { DropdownEmitter } from '../DrillDownMap'
import { ListDrillDownEvent } from '../classes/ListDrillDownEvent'
import { DrillDownEvent, ParamsDrillDownEvent } from '../types'
import { createGradientArray, maxPossibleColorsOnGradient } from '../utils/GradiateUtils'
import { EventEmitter } from 'ahooks/lib/useEventEmitter'
import { centerPoints } from '../utils/config-map'
import { LatLng } from '../classes/Geocoding'
import { DEFAULT_POLYLINE_WEIGHT, SELECTED_POLYLINE_WEIGHT } from '../components/PointInformation/utils'

type IPolyline = {
    [key: string]: {
        polyline: google.maps.Polyline
        event?: DrillDownEvent
    }
}

let polylines: IPolyline = null
const gradientArray = []
let listDrillDownEvent: ListDrillDownEvent
let colorMap = {}
let currentVisibleSelectedPolyline = { id: null, zIndex: null }
let hasCentered = false

export const usePolyline = (
    map: google.maps.Map,
    emitter?: EventEmitter<DropdownEmitter>
): {
    createPolyline: (drillDowEvents: DrillDownEvent[], gradientColorStart: string, gradientColorEnd: string) => void
    changeVisiblePolylines: (lookupKey?: string, defaultColor?: string) => void
    selectPolyline: (id: string) => void
    gradientArray: string[]
} => {
    const clearPolylines = (): void => {
        if (polylines) {
            Object.keys(polylines).forEach(key => {
                polylines[key].polyline.setMap(null)
            })
        }
        polylines = null
    }

    const buildPolylineReferenceAndPaths = (
        drillDownEvents: DrillDownEvent[],
        colorMap: Record<number, string>
    ): { polylines; extremes: { southWest; northEast } } => {
        const polylines = {}
        const extremes = {
            northEast: { lat: 0, lng: 0 },
            southWest: { lat: 0, lng: 0 }
        }
        listDrillDownEvent = new ListDrillDownEvent(drillDownEvents)
        const eventsLength = drillDownEvents.length

        const arrayIndexMedian = Math.floor((eventsLength - 1) / 2)

        const isArrayEven = eventsLength % 2 === 0

        let leftPointer = arrayIndexMedian
        let rightPointer = arrayIndexMedian

        if (isArrayEven) {
            createPolylineInstance(
                drillDownEvents[arrayIndexMedian],
                drillDownEvents[arrayIndexMedian + 1],
                arrayIndexMedian,
                polylines,
                extremes,
                colorMap
            )
            leftPointer = arrayIndexMedian - 1
            rightPointer = arrayIndexMedian + 2
        }

        while (leftPointer > 0 && rightPointer < eventsLength - 1) {
            if (leftPointer === rightPointer) {
                createPolylineInstance(
                    drillDownEvents[arrayIndexMedian - 1],
                    drillDownEvents[arrayIndexMedian],
                    arrayIndexMedian - 1,
                    polylines,
                    extremes,
                    colorMap
                )
                createPolylineInstance(
                    drillDownEvents[arrayIndexMedian],
                    drillDownEvents[arrayIndexMedian + 1],
                    arrayIndexMedian,
                    polylines,
                    extremes,
                    colorMap
                )
            } else {
                createPolylineInstance(
                    drillDownEvents[leftPointer - 1],
                    drillDownEvents[leftPointer],
                    leftPointer - 1,
                    polylines,
                    extremes,
                    colorMap
                )
                createPolylineInstance(
                    drillDownEvents[leftPointer],
                    drillDownEvents[leftPointer + 1],
                    leftPointer,
                    polylines,
                    extremes,
                    colorMap
                )
                createPolylineInstance(
                    drillDownEvents[rightPointer - 1],
                    drillDownEvents[rightPointer],
                    rightPointer - 1,
                    polylines,
                    extremes,
                    colorMap
                )
                createPolylineInstance(
                    drillDownEvents[rightPointer],
                    drillDownEvents[rightPointer + 1],
                    rightPointer,
                    polylines,
                    extremes,
                    colorMap
                )
            }

            leftPointer -= 2
            rightPointer += 2
        }

        return { polylines, extremes }
    }

    const createPolylineInstance = (
        pathStart: DrillDownEvent,
        pathEnd: DrillDownEvent,
        pathsMapIndex: number,
        polylines,
        extremes,
        colorMap: Record<number, string>
    ): void => {
        const { lat: startLat, lng: startLng, id } = pathStart || { lat: undefined, lng: undefined }
        const { lat, lng, levelColor } = pathEnd || { lat: undefined, lng: undefined }
        const latLngValid = lat && lng
        const previousLatLngValid = startLat && startLng

        const colorMapKeys = Object.keys(colorMap)
        const color = colorMapKeys.find(key => pathsMapIndex <= Number(key))
            ? colorMap[colorMapKeys.find(key => pathsMapIndex <= Number(key)) || colorMapKeys.length - 1]
            : '#000000'

        // An older polyline is always on top of a newer one.
        // This way the gradient will be displayed correctly and not with a "zebra" when points are the same or to close
        const polyLineZIndex = google.maps.Marker.MAX_ZINDEX - 1 - pathsMapIndex

        if (latLngValid && previousLatLngValid) {
            const polyline = new google.maps.Polyline({
                path: [
                    { lat: startLat, lng: startLng },
                    { lat, lng }
                ],
                strokeColor: levelColor || color,
                strokeOpacity: 1,
                strokeWeight: DEFAULT_POLYLINE_WEIGHT,
                zIndex: polyLineZIndex > 0 ? polyLineZIndex : 2,
                map
            })

            try {
                polyline.addListener('click', () => {
                    if (emitter) emitter.emit({ event: pathStart })
                })

                polylines[id] = { polyline, event: pathStart, polyLineZIndex }
            } catch (error) {
                console.log('ERROR ON EMMITER: ', error)
            }

            checkExtremePoints(lat, lng, extremes)
            checkExtremePoints(startLat, startLng, extremes)
        }
    }

    const selectPolyline = (id): void => {
        if (polylines?.[currentVisibleSelectedPolyline?.id]) {
            polylines[currentVisibleSelectedPolyline.id].polyline.setOptions({
                strokeWeight: DEFAULT_POLYLINE_WEIGHT,
                zIndex: currentVisibleSelectedPolyline.zIndex
            })
        }

        const polylinesIds = Object.keys(polylines).map(p => +p)

        //TODO: REVISIT THIS TO SEE IF IT IS STILL NEEDED
        // If the id is not in the polylines, we will find the closest one to avoid errors
        if (!polylinesIds.includes(+id)) {
            const closestId = polylinesIds.reduce((prev, curr) =>
                Math.abs(curr - id) < Math.abs(prev - id) ? curr : prev
            )
            id = closestId.toString()
        }
        const zIndex = polylines[id].polyline.get('zIndex')
        polylines[id].polyline.setOptions({
            strokeWeight: SELECTED_POLYLINE_WEIGHT,
            zIndex: google.maps.Marker.MAX_ZINDEX
        })
        currentVisibleSelectedPolyline = { id, zIndex }
    }

    const createPolyline = (drillDowEvents: DrillDownEvent[], gradientColorStart, gradientColorEnd): void => {
        if (map && drillDowEvents) {
            clearPolylines()
            const maxColors = maxPossibleColorsOnGradient(gradientColorStart, gradientColorEnd)
            const sameColorAmount = Math.ceil(drillDowEvents.length / maxColors)
            const gradientArray = createGradientArray(gradientColorStart, gradientColorEnd, maxColors)
            colorMap = []
            for (let i = 0; i < gradientArray.length; i++) colorMap[sameColorAmount * (i + 1)] = gradientArray[i]

            const polylineAndPath = buildPolylineReferenceAndPaths(drillDowEvents, colorMap)

            polylines = polylineAndPath.polylines

            if (!hasCentered) {
                centerPoints(map, polylineAndPath.extremes)
                hasCentered = true
            }
        }
    }

    const checkExtremePoints = (lat, lng, extremes: { southWest: LatLng; northEast: LatLng }): void => {
        if (lat > extremes.northEast.lat || +extremes.northEast.lat === 0) extremes.northEast.lat = lat

        if (lat < extremes.southWest.lat || +extremes.southWest.lat === 0) extremes.southWest.lat = lat

        if (lng > extremes.northEast.lng || +extremes.northEast.lng === 0) extremes.northEast.lng = lng

        if (lng < extremes.southWest.lng || +extremes.southWest.lng === 0) extremes.southWest.lng = lng
    }

    const changeVisiblePolylines = (lookupKey?: string, defaultColor?: string): void => {
        if (polylines && listDrillDownEvent) {
            Object.keys(polylines).forEach(key => {
                const { polyline, event } = polylines[key]
                if (lookupKey) {
                    const param: ParamsDrillDownEvent = listDrillDownEvent.getByLookup(event, lookupKey)
                    polyline.setOptions({ strokeColor: param.color, strokeOpacity: 1 })
                } else {
                    const colorMapKeys = Object.keys(colorMap)
                    const strokeColor =
                        event?.levelColor ||
                        colorMap[colorMapKeys.find(k => Number(key) <= Number(k)) || colorMapKeys.length - 1] ||
                        defaultColor // If the color is not found, it will use the default color
                    polyline.setOptions({
                        strokeOpacity: 1,
                        strokeColor
                    })
                }
            })
        }
    }

    return { createPolyline, changeVisiblePolylines, selectPolyline, gradientArray }
}
